import nookies from 'nookies';
import NP from 'number-precision';
import qs from 'qs';

import Constants from '@/constants/const';

import dayjs from './dayjs';
import { isLatinoChannel } from './common/latino-app';

export const isServer: boolean = typeof window === 'undefined';

/**
 * 格式化金额
 * @param amount
 */
export const moneyFormat = amount => {
  return amount && amount !== 'undefined' ? NP.round(amount, 2).toFixed(2) : '0.00';
};

/**
 * format number
 * @param num
 */
export const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US', { style: 'decimal' }).format(num);
};

/**
 * format number with sign
 * @param num
 */
export const formatNumberWithSign = (num: number) => {
  const sign = num > 0 ? '+' : '';
  const displayStr = new Intl.NumberFormat('en-US', { style: 'decimal' }).format(num);
  return sign + displayStr;
};

/**
 * format money
 * @param amount
 */
export const formatMoney = (amount: number, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

/**
 * 获取dayjs对象，支持多语言
 * @param input
 */
export const getDayjsObject = (input: string | number, locale: string) => {
  const lng = locale === 'zh' ? 'zh-cn' : locale === 'zht' ? 'zh-tw' : locale;
  return dayjs(input).locale(lng);
};

/**
 *
 * @param lang 转发语言
 */
export const switchLanguage = lang => {
  if (lang === Constants.LANGUAGE_ZH_ALIAS) {
    return Constants.LANGUAGE_ZH;
  }
  if (lang === Constants.LANGUAGE_ZHT_ALIAS) {
    return Constants.LANGUAGE_ZHT;
  }
  if (!Constants.LANGUAGES.includes(lang)) {
    return Constants.LANGUAGE_EN;
  }
  return lang;
};

/**
 * 获取cookies
 * @param key
 * @param ctx client端为空
 */
export const getCookie = (key: string, ctx?) => {
  const cookies = nookies.get(ctx || {}) || {};
  return cookies[key];
};

export const getCookieObj = (key: string, ctx = {}) => {
  return JSON.parse(getCookie(key, ctx));
};

export const setCookie = (key: string, value: string, ctx?, config?) => {
  config = config ?? {};
  const cookies =
    nookies.set(ctx || {}, key, value, {
      ...config,
      path: '/',
      domain: process.env.DOMAIN_HOST || process.env.NEXT_PUBLIC_DOMAIN_HOST
    }) || {};
  return cookies[key];
};

export const deleteCookie = (key: string, ctx = {}) => {
  nookies.destroy(ctx, key, {
    path: '/',
    domain: process.env.DOMAIN_HOST || process.env.NEXT_PUBLIC_DOMAIN_HOST
  });
};

/**
 * 将数组按照index拆分为奇偶两个集合
 * @param items
 */
export const splitToOddAndEven = (items: Array<any>) => {
  const result = { odd: [], even: [] };
  if (items?.length) {
    items.forEach((item, index) => {
      if ((index + 1) % 2 == 0) {
        result.even.push(item);
      } else {
        result.odd.push(item);
      }
    });
  }
  return result;
};

/**
 * 格式化
 * @param number 要格式化的数字
 * @param n 保留的小位数
 * @param x 几个数字一次间隔
 */
export const wrapCurrency = (num, n = 2, x = 3) => {
  const nu = new Number(num ?? 0);
  const re = '\\d(?=(\\d{' + x + '})+' + (n > 0 ? '\\.' : '$') + ')';
  return nu.toFixed(Math.max(0, ~~n)).replace(new RegExp(re, 'g'), '$&,');
};

export const buildUrlWithOrigin = (path: string) => {
  const host = process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST;
  if (path.indexOf(host) === -1) {
    if (path[0] === '/') {
      return `${host}${path || ''}`;
    } else {
      return `${host}/${path || ''}`;
    }
  } else {
    return path;
  }
};

/**
 * 判断是否app打开
 */
export const isSayWeeeApp = function (ctx?) {
  const ua = getUa(ctx);
  if (ua) {
    return ua.indexOf('weeeapp') > -1 || ua.indexOf('masgustoapp') > -1;
  } else {
    return false;
  }
};

/**
 * 是否是ios 不是ios就是安卓
 * @param ctx
 */
export const isIOS = function (ctx?) {
  const ua = getUa(ctx);
  if (ua) {
    return !!ua?.match(/(iPad|iPhone|iPod)/i);
  } else {
    return false;
  }
};

/**
 *
 * @param ctx ios的safari
 */
export const isSafari = function (ctx?) {
  const ua = getUa(ctx).toLowerCase();
  if (ua) {
    return ua.indexOf('safari') > -1 && ua.indexOf('chrome') == -1;
  } else {
    return false;
  }
};

// ios userAgent demo
// 'Mozilla/5.0 (iPhone; CPU iPhone OS 14_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 WeeeApp 12.1.1118 (en) 6007D1D8-6572-49DA-BD4B-F20106F696DE (API)'

// android userAgent demo
//'Mozilla/5.0 (Linux; Android 11; SM-G7810 Build/RP1A.200720.012; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/90.0.4430.82 Mobile Safari/537.36 WeeeApp 11.4.1100 (en) c344fa21-9bb5-4b09-939a-375bad680712 (API)'

/**
 *
 * @param ctx
 * ios12.0 android 14.3 支持原生分享
 */
export const isSupportAppShare = (ctx?) => {
  const ua = getUa(ctx);
  const isWeeeIosApp = isSayWeeeApp(ctx) && isIOS(ctx);
  const isWeeeAndroidApp = isWeeeAndroid(ctx);
  const { majorVersion, minorVersion, minVersion } = getAppVersionNum(ctx);
  const iosVersionValid = isWeeeIosApp && ((majorVersion === 12 && minorVersion >= 0) || majorVersion >= 13);
  const androidVersionValid = isWeeeAndroidApp && ((majorVersion === 14 && minorVersion >= 3) || majorVersion >= 15);
  const versionValid = ua && (iosVersionValid || androidVersionValid);
  return !!versionValid;
};

export const getUa = (ctx?) => {
  const ua =
    typeof window === 'undefined'
      ? ctx?.req?.headers?.['user-agent']?.toLowerCase()
      : window?.navigator?.userAgent?.toLowerCase();
  return ua || '';
};

export const getAppVersionNum = (ctx?) => {
  const ua = getUa(ctx);
  const version = ua?.match(/(?:weeeapp|masgustoapp)\s([\d.]+)/i)?.[1];
  const [majorVersion = 0, minorVersion = 0, minVersion = 0] = (version || '').trim().split('.');
  return {
    majorVersion: +majorVersion,
    minorVersion: +minorVersion,
    minVersion: +minVersion
  };
};

/**
 *
 * @param version 当前版本是否大于或者等于传入的版本
 * @param ctx
 */
export const isAboveVersion = (version: string, ctx?): boolean => {
  const { majorVersion, minorVersion, minVersion } = getAppVersionNum(ctx);

  let [comparedMajorVersion = 0, comparedMinorVersion = 0, comparedMinVersion = 0] = (version || '').split('.');
  if (majorVersion > +comparedMajorVersion) {
    return true;
  }
  if (majorVersion === +comparedMajorVersion && minorVersion > +comparedMinorVersion) {
    return true;
  }
  if (
    majorVersion === +comparedMajorVersion &&
    minorVersion === +comparedMinorVersion &&
    minVersion >= +comparedMinVersion
  ) {
    return true;
  }
  return false;
};

/**
 * 对比两个平台的代码,如果平台是H5，返回false
 * @param versions 两个平台分别的版本，数组的第一项是ios的版本 第二项是android的版本
 * @param ctx
 * @returns
 */
export const isAboveVersions = (versions: string[], ctx?) => {
  let isIos = isIOS(ctx);
  let paltformConditions = [isIos, !isIos];
  if (!isSayWeeeApp(ctx)) {
    return false;
  }
  return versions.some((platformVersion, index) => {
    return paltformConditions[index] && isAboveVersion(platformVersion, ctx);
  });
};

/** 获取完整app版本号 */
export const getWholeAppVersion = (ctx?): string => {
  const ua = getUa(ctx);
  let appVersion = (ua?.match(/(?:weeeapp|masgustoapp)\s([\d.]+)/i)?.[1] || '').trim() || '';
  return appVersion;
};

/**
 * 獲取app的版本
 * @param ctx
 * @returns
 */
export const getAppVersion = (ctx?): string => {
  const ua = getUa(ctx);
  let appVersion = (ua?.match(/(?:weeeapp|masgustoapp)\s([\d.]+)/i)?.[1] || '').trim() || '';
  return appVersion.split('.').slice(0, -1).join('.') || null;
};

/**
 * weee app的安卓手机
 */
export function isWeeeAndroid(ctx?) {
  const result = isSayWeeeApp(ctx) && !isIOS(ctx);
  return result;
}

/**
 * 判断是否是微信游览器
 */
export function isWeixinBrowser(ctx?) {
  const ua = getUa(ctx);
  return /micromessenger/i.test(ua) || /windows phone/i.test(ua);
}

/**
 * 判断是否为安卓手机
 */
export function isAndroid(ctx?) {
  const ua = getUa(ctx);
  return /android/i.test(ua);
}

/**
 * 判断是否是手机浏览器
 */
export function isMobileBrowser(ctx?) {
  const ua = getUa(ctx);
  // return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera|Mini/i.test(ua);
  return /mobile|nokia|iphone|android|samsung|htc|blackberry|WAP/i.test(ua);
}

/**
 * 判断是否在抖音客户端打开
 */
export function isTiktokBrowser(ctx?) {
  const ua = getUa(ctx);
  return /musical|trill/i.test(ua);
}

/**
 * 判断是否在facebook客户端打开
 */
export function isFacebookBrowser(ctx?) {
  const ua = getUa(ctx);
  return /(FBAN|FBAV)/i.test(ua);
}

/**
 * weee-img, weee-dev-img的图片加cdn前缀
 * cdn01.sayweee.net => images01
 * s3 => images01
 * cdn.sayweee.net 不处理
 * @param url
 * @param size 裁切的尺寸，只支持正方形
 */

const CDN_MATCHERS = new Map([
  [
    /^[https|http]*:\/\/(.*?)\.s3(\.|\-)us-west-\d\.amazonaws\.com\/.*?\.[jpg|gif]/i,
    (imgUrl, size, matcher) => {
      imgUrl = imgUrl.substr(0, imgUrl.lastIndexOf('.'));
      imgUrl = imgUrl.split('.com')[1];
      imgUrl = imgUrl.replace(/(.*)-(\d)+$/, '$1');
      imgUrl = imgUrl.replace('-square', '');
      const match = matcher[1];
      if (match === 'weee-img') {
        imgUrl = `${Constants.IMAGE_CDN.PROD}${imgUrl}-square-${size || 320}.jpg`;
      } else {
        imgUrl = `${Constants.IMAGE_CDN.DEV}${imgUrl}-square-${size || 320}.jpg`;
      }
      return imgUrl;
    }
  ],
  [
    // /^[https|http]*:\/\/cdn01\.sayweee\.net\/(.*)?-\d*\.[jpg|gif]/i,
    /^[https|http]*:\/\/cdn(01)?\.sayweee\.net\/(.*)\.(jpg|gif)/i,
    (imgUrl, size, matcher) => {
      let url = matcher[2] || '';
      const ext = matcher[3];
      url = url.substr(0, imgUrl.lastIndexOf('.'));
      url = url.replace(/^http(s)?:\/\//, '');
      url = url.replace(/(.*)-(\d)+$/, '$1');
      url = url.replace('-square', '');
      return `${Constants.IMAGE_CDN.PROD}/${url}-square-${size || 320}.${ext}`;
    }
  ]
]);

export const getCDNUrl = (url, size?) => {
  let imgUrl = url || '';
  CDN_MATCHERS.forEach((value, key, map) => {
    const match = imgUrl.match(key);
    if (match) {
      imgUrl = value?.(imgUrl, size, match);
      return imgUrl;
    }
  });
  return imgUrl;
};

export const isRobot = (ctx?) => {
  const ua = getUa(ctx);
  return [
    'googlebot',
    'msnbot',
    'slurp',
    'yahoo',
    'askjeeves',
    'fastcrawler',
    'infoseek',
    'lycos',
    'whatsapp',
    'kakaotalk-scrap',
    'facebookexternalhit',
    'alexabot',
    'Baidu-YunGuanCe-SLABot',
    'bing',
    'YandexDotBot',
    'SemrushBot',
    'curl Weee v1.0',
    'istellabot',
    'crawler',
    'spider',
    'robot',
    'bot/',
    'adsbot',
    'python-requests',
    'ELB-HealthChecker',
    'masscan',
    'Python-urllib',
    'SFDC-Callout/49.0',
    'cortex/1.0',
    'adreview/1.0',
    'SFDC-Callout/50.0',
    'TelegramBot',
    'via ggpht.com GoogleImageProxy',
    'ltx71 - (http://ltx71.com/)',
    'petalbot'
  ].some(robot => {
    return !!ua?.match(robot.toLowerCase());
  });
};

export const isMobile = isMobileBrowser;

/**在微信浏览器打开 */
export const isWechatBrowser = isWeixinBrowser;

export const isAlipayBrowser = function (ctx?) {
  const ua = getUa(ctx);
  return /alipay/i.test(ua);
};

export const isKaKaoBrowser = function (ctx?) {
  const ua = getUa(ctx);
  return /kakaotalk/i.test(ua);
};

export const isFBBrowser = function (ctx?) {
  const ua = getUa(ctx);
  return /fba(n|v)/i.test(ua);
};

/**
 *
 * @param path
 */
export const isReferer = path => {
  return document?.referrer?.toLowerCase().indexOf(path) > -1;
};

export const getOrigin = (ctx?) => {
  if (isServer) {
    const req = ctx?.req;
    return `${req?.protocol}://${req?.hostname}`;
  }
  return window.location.origin;
};

//获取访问路径
export const getUrl = (ctx?) => {
  if (isServer) {
    const req = ctx?.req || {};
    return req.protocol + '://' + req.get('host') + req.originalUrl;
  }
  return window.location.href;
};

/**
 * 空函数
 */
export const noop = () => {
  return void 0;
};

export const getLoginState = (ctx?) => {
  // let login = false;
  // token = token || getCookie(Constants.AUTH_TOKEN);
  // try {
  //   login = !!jsonwebtoken.decode(token).is_login;
  // } catch (e) {
  //   logger.info({
  //     info: 'parse token error',
  //     token: token
  //   });
  // }
  return !!(getValueFromCookie(Constants.IS_LOGIN, ctx) === '1');
};

interface utilsProps {
  isObject?: (args: any) => boolean;
  isString?: (args: any) => boolean;
  isNumber?: (args: any) => boolean;
  isUndefined?: (args: any) => boolean;
  isArray?: (args: any) => boolean;
  isBoolean?: (args: any) => boolean;
  isFunction?: (args: any) => boolean;
}

const utils: utilsProps = {};

['Object', 'String', 'Number', 'Undefined', 'Array', 'Boolean', 'Function'].forEach(type => {
  utils[`is${type}`] = function (args: any): boolean {
    return Object.prototype.toString.call(args) === `[object ${type}]`;
  };
});

export const toString = newQuery => {
  return qs.stringify(newQuery, { addQueryPrefix: true, encodeValuesOnly: true });
};

export default utils;

export const getPathNoLang = url => {
  return url.replace(Constants.LANGUAGE_REGEXP_TWO, '');
};

export const getUrlQuery = query => {
  if (isServer) return '';
  const queryValue = qs.parse(window.location.href?.split('?')?.[1]?.split('#')?.[0])?.[query] ?? '';
  return queryValue;
};

/**
 * 如果url上有同名query， qs.parse 格式化后会返回一个数组
 * 如果是数组暂时只处理为取数组第一项
 * @param value 某一query的值
 * @returns
 */
export const getSafeQueryValue = value => {
  if (!value) {
    return '';
  }
  if (Array.isArray(value)) {
    return value[0] ? String(value[0]) : '';
  }
  return String(value);
};

export function updateSessionTokenMaxAge() {
  const sessionToken = getCookie(Constants.WEEE_SESSION_TOKEN);
  if (!!sessionToken) {
    setCookie(Constants.WEEE_SESSION_TOKEN, sessionToken, null, { maxAge: Constants.WEEE_SESSION_TOKEN_MAX_AGE });
  }
}

export function getPathNoLangOnClient(url?: string) {
  if (!window) return '';
  const pathname = url || window.location.pathname;
  const pathNoLang = pathname.replace(Constants.LANGUAGE_REGEXP_TWO, '');
  if (pathNoLang.indexOf('/') !== 0) {
    return '/' + pathNoLang;
  }
  return pathNoLang;
}

export const omit = (query = {}) => {
  const result = Object.keys(query).reduce((pre, cur) => {
    if (query[cur]) {
      pre[cur] = query[cur];
    }
    return pre;
  }, {});
  return result;
};

//解决移动端滚动穿透问题
export const fixedBody = () => {
  const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
  document.body.style.cssText += 'position:absolute;width:100%;top:-' + scrollTop + 'px;';
};

export const looseBody = () => {
  const body = document.body;
  body.style.position = '';
  const top = body.style.top;
  document.body.scrollTop = document.documentElement.scrollTop = -parseInt(top);
  body.style.top = '';
};
/**
 * 根据给与key值比较对象数组中的元素是否相同(对象数组中只要key值相同，返回true，否则false)
 * @param sourceArr 待比较数组
 * @param targetArr 目标数组
 * @param key 需要对比对象的属性key
 * @returns {boolean}
 */
export const isEqualArrayWithTargetKey = (sourceArr: any[] = [], targetArr: any[] = [], key: string): boolean => {
  /**
   * 只在两个数组不为空并且长度相等时进行key值的比较，其他情况均返回false
   */
  try {
    if ((!sourceArr && !targetArr) || (sourceArr.length === 0 && targetArr.length === 0)) {
      return false;
    }

    if (sourceArr.length !== targetArr.length) {
      return true;
    }

    let res = false;
    if (sourceArr && targetArr && sourceArr.length && targetArr.length === sourceArr.length) {
      for (let x = 0; x < sourceArr.length; x++) {
        const sourceValue = sourceArr[x][key];
        const targetValue = targetArr.filter(item => item[key] === sourceValue);
        if (targetValue && targetValue.length) {
          if (x === sourceArr.length - 1) {
            res = true;
          }
          continue;
        }
      }
    }
    return res;
  } catch (error) {
    return false;
  }
};

export const isEmptyStr = (str: string) => {
  if (typeof str === 'undefined' || str === null) return true;
  return !str.toString().length;
};

export function filterStringEmoji(str: string) {
  if (typeof str !== 'string') return '';
  return str.replace(
    /[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF][\u200D]|[\uFE0F]|[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF]|[0-9|*|#]\uFE0F\u20E3|[0-9|#]\u20E3|[\u203C-\u3299]\uFE0F\u200D|[\u203C-\u3299]\uFE0F|[\u2122-\u2B55]|\u303D|[\A9|\AE]\u3030|\uA9|\uAE|\u3030/gi,
    ''
  );
}

/**
 * 判断是否为空对象
 * @param obj
 * @returns
 */
export const isEmptyObject = (obj: { [key: string]: any } | undefined | null) => {
  if (obj === null || obj === undefined || obj.constructor !== Object || Object.keys(obj).length < 1) {
    return true;
  }
  return false;
};
/*
 * 返回languages的key-value对象
 */
export function allLanguageLabels() {
  const allLanguages = isLatinoChannel() ? Constants.LATINO_LANUAGES : Constants.ALL_LANGUAGES;
  const allLanguagesMap = allLanguages.reduce((pre, cur) => {
    pre[cur.key] = cur.label;
    return pre;
  }, {});
  return allLanguagesMap;
}
/**
 * store 参数需要在后面访问时去掉
 * @param paramsArray 需要从url上剔除哪些参数然后刷新页面
 */
export const reloadPageAndRemoveParams = (paramsArray?: string[], addParams?: ObjectType) => {
  let params = qs.parse(window.location.search.slice(1));
  if (Array.isArray(paramsArray)) {
    paramsArray.forEach(str => {
      if (params[str]) {
        delete params[str];
      }
    });
  }
  if (params['grocery-store']) {
    delete params['grocery-store']; //这个一定要删除的
  }
  if (params['set_lang']) {
    delete params['set_lang']; //这个一定要删除的
  }
  params = {
    ...params,
    ...addParams
  };
  const url = `${window.location.origin}${window.location.pathname}${qs.stringify(params, {
    addQueryPrefix: true
  })}`;
  window.location.href = url;
};

/**
 * 升序排序对象数组，按照给定key的value进行排序
 * @param arr 待排序数组
 * @param key 对象的属性key
 * @param reverse 不传或false为正序排序，true为倒序排序
 * @returns {Array}
 */
export const sortByTargetKey = (arr: any[], key: string, reverse?: boolean): any[] => {
  if (arr && arr.length) {
    for (let x = 0; x < arr.length - 1; x++) {
      for (let y = 0; y < arr.length - 1 - x; y++) {
        if (reverse) {
          if (parseInt(arr[y][key]) < parseInt(arr[y + 1][key])) {
            [arr[y], arr[y + 1]] = [arr[y + 1], arr[y]];
          }
        } else {
          if (parseInt(arr[y][key]) > parseInt(arr[y + 1][key])) {
            [arr[y], arr[y + 1]] = [arr[y + 1], arr[y]];
          }
        }
      }
    }
  }
  return arr;
};

/**
 * 获取直邮满运费门槛，比如满49/35
 */
export const getShippingFreeFee = () => {
  return getCookie(Constants.PANTRY_FREE_FEE);
};

export const getGlobalFreeFee = () => {
  return getCookie(Constants.GLOBAL_FREE_FEE);
};

/**
 * 获取生鲜邮费，比如说5.99/3.99
 */
export const getPantryShippingFee = () => {
  return getCookie(Constants.PANTRY_FEE);
};

/**
 * 兼容日文下格式化周的字符串会出现样式问题
 * @param lang 语言
 * @param date 需要格式化的日期
 * @returns string
 */
export const compatibleDayJsFormat = (lang: string, date: string) => {
  if (!date) return '';
  //日文下展示兼容
  const dateFormat = lang === Constants.LANGUAGE_JA ? 'dddd MM/DD/YYYY' : 'ddd MM/DD/YYYY';
  return getDayjsObject(date, lang).format(dateFormat);
};

/**
 * Add param before others 在地址上添加query
 * @param url
 * @param parameterName
 * @param parameterValue
 * @param atStart
 *
 * @returns
 */
export function addParameterToUrl(url, parameterName, parameterValue, atStart?) {
  const replaceDuplicates = true;
  let urlhash, cl;
  if (url.indexOf('#') > 0) {
    cl = url.indexOf('#');
    urlhash = url.substring(url.indexOf('#'), url.length);
  } else {
    urlhash = '';
    cl = url.length;
  }
  const sourceUrl = url.substring(0, cl);

  const urlParts = sourceUrl.split('?');
  let newQueryString = '';

  if (urlParts.length > 1) {
    const parameters = urlParts[1].split('&');
    for (let i = 0; i < parameters.length; i++) {
      const parameterParts = parameters[i].split('=');
      if (!(replaceDuplicates && parameterParts[0] == parameterName)) {
        if (newQueryString === '') newQueryString = '?';
        else newQueryString += '&';
        newQueryString += parameterParts[0] + '=' + (parameterParts[1] ? parameterParts[1] : '');
      }
    }
  }
  if (newQueryString === '') newQueryString = '?';

  if (atStart) {
    newQueryString =
      '?' + parameterName + '=' + parameterValue + (newQueryString.length > 1 ? '&' + newQueryString.substring(1) : '');
  } else {
    if (newQueryString !== '' && newQueryString != '?') newQueryString += '&';
    newQueryString += parameterName + '=' + (parameterValue ? parameterValue : '');
  }
  return urlParts[0] + newQueryString + urlhash;
}

/**
 * 解析token
 */
export const decodeToken = () => {
  const token = getCookie('auth_token');
  if (typeof token !== 'string') {
    return {};
  }
  const base64Url = token?.split('.')[1];
  const base64 = (base64Url && base64Url?.replace(/-/g, '+').replace(/_/g, '/')) || '';
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
  return jsonPayload ? JSON.parse(jsonPayload) : {};
};

/**
 * 判断当前页面是否有可以back的页面
 * Tips:只能应用在客户端
 * */
export const isNotPageCanBack = () => {
  if (typeof window === 'undefined' || !document || !history || !history.state) return false;
  // 获取next记录的history idx，第一个页面为0
  const historyState = history?.state?.idx;
  return !document?.referrer && historyState === 0;
};

export function formatLocale(lang: string) {
  let locale = lang;
  switch (lang) {
    case Constants.LANGUAGE_CHINESE_T:
      locale = Constants.LANGUAGE_CHINESE_ALTERNATE;
      break;
    default:
      break;
  }
  return locale;
}

export function notNaNUndefinedNum(num) {
  return !(isNaN(num) || num === undefined || num === null);
}

export const getUniqueId = () => {
  return Number(Math.random().toString().substr(3, 20) + Date.now()).toString(36);
};

export const formatDateByRule = (date: string, formatRule: string, local: string) => {
  const dateObj = getDayjsObject(date, local);
  return dateObj.format(formatRule);
};

export const trimAllSpaces = (str: string) => {
  if (typeof str !== 'string') return str;
  return str.replace(/\s*/g, '');
};

/**
 * string 类型的日期排序, eg: "2022-01-01"
 * @param arr 数组
 * @param splitSign 日期的分隔符，当前工程里大多使用的是'-'
 * @returns
 */
export const sortStringDate = (arr: string[], splitSign?: string) => {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return [];
  const sign = splitSign || '-';
  return arr?.sort(function (a, b) {
    a = a.split(sign).join('');
    b = b.split(sign).join('');
    return a > b ? 1 : a < b ? -1 : 0;
  });
};

export const notNullUndefined = val => {
  return val !== null && val !== undefined;
};

/**
 * 解决 region_id=%E2%80%8B%E2%80%8B000001 parseInt 出来还是NaN
 * @param {*} err
 */
export const parseUnExceptedStr = str => {
  if (!str) return str;
  if (isNaN(parseInt(str))) {
    return str.slice(2);
  }
  return str;
};

/**
 *  根据订单状态获取订单状态文案
 * @param vendor_id
 * @returns
 */
export const getOrderNoteFromLocalStorage = vendor_id => {
  if (typeof window === 'undefined') return '';
  return (JSON.parse(localStorage?.getItem(Constants.ORDER_NOTE)) || '{}')?.[vendor_id] || '';
};

/*
 * 获取location.hash
 */
export const getLocationHash = () => {
  if (typeof window === 'undefined') return null;
  return window?.location?.hash;
};

export function convertObjectToUrlParams<T extends { [key: string]: string | number }>(obj: T): string {
  if (!obj || Object.keys(obj).length < 1) {
    return '';
  }

  const searchStr = Object.keys(obj)
    .map(item => (obj[item] ? `${item}=${obj[item]}` : ''))
    .filter(param => param !== '')
    .join('&');

  if (!searchStr) return '';

  return `?${searchStr}`;
}

/**
 * 判断是否是教育邮箱
 * @param email
 * @returns
 */
export const isEducationEmail = (email: string) => {
  return email?.toLowerCase().endsWith('.edu');
};

export const getValueFromCookie = (key: string, ctx?) => {
  return ctx?.req?.[key] || getCookie(key, ctx);
};
/**
 * php微信登陆后绑定页面跳到重定向页面并跳出绑定弹框
 * params: popUpType 弹框类型 手机邮箱绑定或者手机绑定
 */

export const connectPopUpRedirectInWechat = (popUpType: 'showBindConnetPopUp' | 'showPhoneConnectPopUp', ctx?) => {
  const isInWechatBrowser = isWeixinBrowser(ctx);
  if (!isInWechatBrowser) return;
  const resolvedUrl = ctx?.resolvedUrl;
  const refer = resolvedUrl?.split('?')[1];
  if (!refer) return;
  const queryObj = qs.parse(refer);
  const { redirect_url: redirectUrl } = queryObj;
  const redirectSearchStr = redirectUrl?.split('?')?.[1];
  if (!redirectSearchStr) return;
  const redirectUrlRemoveQuery = redirectUrl?.split('?')?.[0];
  const { isLoginSignupPopup } = qs.parse(redirectSearchStr);
  if (isLoginSignupPopup?.startsWith('1')) {
    delete queryObj?.redirect_url;
    queryObj.isLoginSignupPopup = popUpType;
    const searchStr = qs.stringify(queryObj, { addQueryPrefix: true, encodeValuesOnly: true });
    const link = `${redirectUrlRemoveQuery}${searchStr}`;
    return link;
  }
};

/**
 * 过滤emoji表情
 * @param content
 * @returns {string}
 */
export function removeEmoji(content: string) {
  return content.replace(
    /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g,
    ''
  );
}
/*
 * 社区登陆注册弹框在app内跳到登陆页面
 */
export const goCommunityAppLogin = (lang = 'en', show?: () => void) => {
  const isApp = isSayWeeeApp();
  if (!isApp) {
    show();
    return;
  }
  const origin = getOrigin();
  const curUrl = encodeURIComponent(window.location.href);
  window.location.href = `${origin}/${lang}/account/login?redirect_url=${curUrl}`;
};

/**
 * 新版的ios和安卓自己实现了关闭按钮, 这里为了兼容老版本的web端加了个适配, 在这2个版本以下会展示web的
 *
 */
export const checkIsNeedShowWebBottomCloseIcon = (): boolean => {
  if (isSayWeeeApp()) {
    const { majorVersion, minorVersion } = getAppVersionNum();
    if (isIOS) {
      return majorVersion < 13;
    } else {
      return majorVersion < 13 && minorVersion < 7;
    }
  }
  return false;
};

// iOS 14.7 Android 14.0及其以上版本支持 purchaseStatusNotify jscall
export function isAppSupportPurchaseStatusNotify(ctx?) {
  const isWeeeIosApp = isSayWeeeApp(ctx) && isIOS(ctx);
  const isWeeeAndroidApp = isWeeeAndroid(ctx);
  const { majorVersion, minorVersion, minVersion } = getAppVersionNum(ctx);
  const iosVersionValid = isWeeeIosApp && ((majorVersion === 14 && minorVersion >= 7) || majorVersion >= 15);
  const androidVersionValid = isWeeeAndroidApp && ((majorVersion === 14 && minorVersion >= 0) || majorVersion >= 15);
  const versionValid = iosVersionValid || androidVersionValid;
  return !!versionValid;
}

//Android 14.1 iOS 14.8及其以上版本支持 collectStatusNotify 和onPopupWindowDidFinish jscall
export function isAppSupportCollectStatusNotifyAndOnPopupWindowDidFinish(ctx?) {
  const isWeeeIosApp = isSayWeeeApp(ctx) && isIOS(ctx);
  const isWeeeAndroidApp = isWeeeAndroid(ctx);
  const { majorVersion, minorVersion, minVersion } = getAppVersionNum(ctx);
  const iosVersionValid = isWeeeIosApp && ((majorVersion === 14 && minorVersion >= 8) || majorVersion >= 15);
  const androidVersionValid = isWeeeAndroidApp && ((majorVersion === 14 && minorVersion >= 1) || majorVersion >= 15);
  const versionValid = iosVersionValid || androidVersionValid;
  return !!versionValid;
}

export function isAppSupportCollectStatusNotify(ctx?) {
  return isAppSupportCollectStatusNotifyAndOnPopupWindowDidFinish(ctx);
}
export function isAppSupportOnPopupWindowDidFinish(ctx?) {
  return isAppSupportCollectStatusNotifyAndOnPopupWindowDidFinish(ctx);
}

/**
 * 有些地方需要显示匿名用户信息
 *
 */
export function hideUserName(str: string) {
  let newName = '';
  const len = str.length;
  if (len == 1) {
    newName = '*';
  } else if (len <= 6) {
    newName = str.substr(0, 1) + new Array(str.length).join('*');
  } else {
    const regExp2 = new RegExp('(.{1}).*(.{2})', 'g');
    newName = str.replace(regExp2, '$1****$2');
  }
  return newName;
}
